import { App } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type File from '../../../interfaces/File';
import type { CreateFolderPayload } from '../../../interfaces/FileApiService';
import { FileService } from '../../../services/fileApiService';
import { useAppStore } from '../../../store/store';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import { FileTitleForm } from '../FileTitleForm';

const fileService = new FileService();

export interface UseFolderPickerCreateFolderParams {
  /** Parent id for createFolder; undefined = personal root. */
  getParentId: () => string | undefined;
  /** Called after create succeeds (e.g. navigate picker into the new folder). */
  onCreated?: (folder: File) => void;
}

/**
 * Opens FileTitleForm modal and creates a folder under the picker parent (not main files context).
 * On success, emits `onFilesCreated` so the route files list can prepend the row.
 */
export function useFolderPickerCreateFolder({
  getParentId,
  onCreated,
}: UseFolderPickerCreateFolderParams) {
  const { t } = useTranslation();
  const { modal, notification, message } = App.useApp();

  const createFolder = useCallback(
    (title: string) => {
      const payload: CreateFolderPayload = {
        name: title,
        parent: getParentId() || undefined,
      };

      void fileService
        .createFolder(payload)
        .then((response) => {
          const newFolder: File = {
            ...response,
            isUnRead: true,
            isFolder: true,
          };
          useAppStore.getState().events.emit.onFilesCreated({ files: [newFolder] });
          message.success({
            content: t('notifications.folderCreated', {
              resource: title,
            }),
          });
          onCreated?.(newFolder);
        })
        .catch((error) => {
          if (handleFilePasswordError(error)) {
            return;
          }
          notification.error({
            title: t('errors.genericCreateError', {
              resource: t('common.folder'),
            }),
          });
        });
    },
    [getParentId, message, notification, onCreated, t],
  );

  const openCreateFolder = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('modal.fileTitleForm.newFolderTitle'),
      content: (
        <FileTitleForm
          isFolder={true}
          onSubmit={(title) => {
            createFolder(title);
            modalInstance.destroy();
          }}
          onCancel={() => {
            modalInstance.destroy();
          }}
        />
      ),
      footer: null,
    });
  }, [createFolder, modal, t]);

  return { openCreateFolder };
}
